import type { AppVueRouter } from '@modules/core/services/router/types';
import type { TranslationKeysMapper } from '@modules/i18n/types';
import type { AppMenu } from '@modules/sidebar/utils';
import { ArticlesEditModule } from '@modules/articles/submodules/edit/ArticlesEditModule';
import { ArticlesViewModule } from '@modules/articles/submodules/view/ArticlesViewModule';
import { AppModule } from '@modules/core/plugins/AppModule';
import { useI18n } from '@modules/i18n/composables';
import {
  ADMIN_PARTNERSHIP_MENU_ITEM,
  AFFILIATE_PARTNERSHIP_MENU_ITEM,
  TRANSLATION_GROUP_SIDEBAR,
} from '@modules/sidebar/constants';

import {
  ADMIN_ARTICLES_LIST_ROUTE_NAME,
  ADMIN_ARTICLES_ROUTE_NAME,
  AFFILIATE_ARTICLES_LIST_ROUTE_NAME,
  AFFILIATE_ARTICLES_ROUTE_NAME,
  ARTICLES_PERMISSIONS,
  TRANSLATION_GROUP_ADMIN_ARTICLES,
  TRANSLATION_GROUP_AFFILIATE_ARTICLES,
} from './constants';
import {
  getAdminTranslationKeys,
  getAffiliateTranslationKeys,
} from './translations';

export class ArticlesModule extends AppModule {
  protected submodules = [
    new ArticlesEditModule(),
    new ArticlesViewModule(),
  ];

  addRoutes(router: AppVueRouter): void {
    router.addAdminRoute({
      name: ADMIN_ARTICLES_ROUTE_NAME,
      path: 'articles',
      permission: ARTICLES_PERMISSIONS.SHOW,
      component: () => import('@modules/core/components/SimpleChildRoutePage/SimpleChildRoutePage.vue'),
      keepAliveInclude: 'ArticlesListPage',
    });

    router.addChildRoute(ADMIN_ARTICLES_ROUTE_NAME, {
      name: ADMIN_ARTICLES_LIST_ROUTE_NAME,
      path: '',
      permission: ARTICLES_PERMISSIONS.SHOW,
      component: () => import('./pages/ArticlesListPage/ArticlesListPage.vue'),
    });

    router.addAffiliateRoute({
      name: AFFILIATE_ARTICLES_ROUTE_NAME,
      path: 'articles',
      permission: ARTICLES_PERMISSIONS.SHOW,
      component: () => import('@modules/core/components/SimpleChildRoutePage/SimpleChildRoutePage.vue'),
      keepAliveInclude: 'ArticlesListPage',
    });

    router.addChildRoute(AFFILIATE_ARTICLES_ROUTE_NAME, {
      name: AFFILIATE_ARTICLES_LIST_ROUTE_NAME,
      path: '',
      permission: ARTICLES_PERMISSIONS.SHOW,
      component: () => import('./pages/ArticlesListPage/ArticlesListPage.vue'),
    });
  }

  addMenu(menu: AppMenu): void {
    const { getTPath } = useI18n();

    menu.addChildAdminMenuItem(ADMIN_PARTNERSHIP_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'ADMIN_PARTNERSHIP_ARTICLES'),
      to: {
        name: ADMIN_ARTICLES_LIST_ROUTE_NAME,
      },
      permission: ARTICLES_PERMISSIONS.SHOW,
      id: ADMIN_ARTICLES_LIST_ROUTE_NAME,
      order: 70,
    });

    menu.addChildAffiliateMenuItem(AFFILIATE_PARTNERSHIP_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'AFFILIATE_PARTNERSHIP_ARTICLES'),
      to: {
        name: AFFILIATE_ARTICLES_LIST_ROUTE_NAME,
      },
      permission: ARTICLES_PERMISSIONS.SHOW,
      id: AFFILIATE_ARTICLES_LIST_ROUTE_NAME,
      order: 1000,
    });
  }

  addTranslationKeys(mapper: TranslationKeysMapper): void {
    mapper.addTranslationsMap(TRANSLATION_GROUP_ADMIN_ARTICLES, getAdminTranslationKeys);
    mapper.addTranslationsMap(TRANSLATION_GROUP_AFFILIATE_ARTICLES, getAffiliateTranslationKeys);
  }
}
